(() => {
  document.querySelectorAll('.ux-home-hero__media--video').forEach((media) => {
    const video = media.querySelector('video');
    const toggle = media.querySelector('[data-hero-video-toggle]');
    if (!video || !toggle) return;
    const update = () => {
      toggle.setAttribute('aria-label', video.paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel);
      toggle.dataset.paused = String(video.paused);
    };
    video.addEventListener('play', update);
    video.addEventListener('pause', update);
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      video.autoplay = false;
      video.pause();
    }
    toggle.addEventListener('click', async () => {
      if (video.paused) {
        try { await video.play(); } catch (error) { update(); }
      } else {
        video.pause();
      }
    });
    update();
    toggle.hidden = false;
  });

  // Header disclosures own only their transient UI state. Application/draft state stays with its owner.
  window.DIMHeaderMenus?.destroy();
  const mobileMenuQuery = '(max-width: 860px)';
  const mobileMenuMedia = window.matchMedia?.(mobileMenuQuery) || { matches: false };
  const mobileMenuBackground = new WeakMap();
  const mobileMenuFocusRevision = new WeakMap();
  const initializedMenus = new Set();
  const initializedWays = new Set();
  const cleanups = new Set();
  const ownedListeners = new Map();
  let nextMenuId = 0;
  const listen = (target, type, handler, options) => {
    target.addEventListener(type, handler, options);
    const cleanup = () => {
      target.removeEventListener?.(type, handler, options);
      cleanups.delete(cleanup);
      ownedListeners.get(target)?.delete(cleanup);
      if (!ownedListeners.get(target)?.size) ownedListeners.delete(target);
    };
    cleanups.add(cleanup);
    if (!ownedListeners.has(target)) ownedListeners.set(target, new Set());
    ownedListeners.get(target).add(cleanup);
  };
  const visible = (control) => !control.closest('[hidden], [inert]') && (!control.getClientRects || control.getClientRects().length);
  const panelFocusableMenuControls = (menu) => Array.from(menu.querySelectorAll('.mobile-menu__panel a[href], .mobile-menu__panel button:not([disabled]), .mobile-menu__panel input:not([disabled]), .mobile-menu__panel select:not([disabled]), .mobile-menu__panel textarea:not([disabled])')).filter(visible);
  const focusableMenuControls = (menu) => [menu.querySelector(':scope > summary'), ...panelFocusableMenuControls(menu), ...Array.from(menu.closest('.site-header')?.querySelectorAll('.header-logo, .header-actions a[href]') || []).filter(visible)].filter(Boolean);
  const isCurrentControl = (candidate, control) => candidate === control || candidate?.isSameNode?.(control) === true;
  const setBackgroundInert = (menu, backdrop) => {
    // The complete header remains active: visitors can still use Help, Basket and the logo.
    const targets = Array.from(document.body.children).filter((child) => child !== backdrop && !child.contains(menu));
    mobileMenuBackground.set(menu, targets.map((target) => ({ target, hadInert: target.hasAttribute('inert') })));
    targets.forEach((target) => target.setAttribute('inert', ''));
  };
  const restoreBackground = (menu) => {
    mobileMenuBackground.get(menu)?.forEach(({ target, hadInert }) => {
      if (!hadInert) target.removeAttribute('inert');
    });
    mobileMenuBackground.delete(menu);
  };
  const focusDesktopEquivalent = (menu) => {
    const active = document.activeElement;
    if (!active || !menu.contains(active)) return;
    const href = active.getAttribute?.('href');
    const header = menu.closest('.site-header');
    const target = (href && Array.from(header?.querySelectorAll('.header-nav a') || []).find((link) => link.getAttribute('href') === href)) || header?.querySelector('.header-nav a');
    target?.focus({ preventScroll: true });
  };
  const closeMenu = (menu, { restoreFocus = false, desktop = false } = {}) => {
    mobileMenuFocusRevision.set(menu, (mobileMenuFocusRevision.get(menu) || 0) + 1);
    menu.removeAttribute('open');
    menu.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    restoreBackground(menu);
    document.querySelector('.mobile-menu__backdrop[data-menu-id="' + menu.dataset.mobileMenuId + '"]')?.remove();
    if (desktop) focusDesktopEquivalent(menu);
    else if (restoreFocus) menu.querySelector(':scope > summary')?.focus({ preventScroll: true });
  };
  const closeWays = (ways, restoreFocus = false) => {
    const panel = ways.querySelector('[data-header-ways-panel]');
    const toggle = ways.querySelector('[data-header-ways-toggle]');
    if (panel) panel.hidden = true;
    toggle?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) toggle?.focus({ preventScroll: true });
  };
  const initHeaderMenus = () => {
    document.querySelectorAll('[data-mobile-menu]').forEach((menu) => {
      if (initializedMenus.has(menu)) return;
      initializedMenus.add(menu);
      menu.dataset.mobileMenuId = String(++nextMenuId);
      listen(menu, 'toggle', () => {
        if (!menu.open) { closeMenu(menu); return; }
        if (!mobileMenuMedia.matches) { closeMenu(menu, { desktop: true }); return; }
        // A second queued toggle must not create another backdrop or replace the original inert snapshot.
        if (mobileMenuBackground.has(menu)) return;
        initializedMenus.forEach((other) => { if (other !== menu && other.open) closeMenu(other); });
        const backdrop = document.createElement('div');
        backdrop.className = 'mobile-menu__backdrop';
        backdrop.dataset.menuId = menu.dataset.mobileMenuId;
        backdrop.setAttribute('aria-hidden', 'true');
        backdrop.addEventListener('click', () => closeMenu(menu, { restoreFocus: true }));
        document.body.append(backdrop);
        setBackgroundInert(menu, backdrop);
        menu.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'true');
        const revision = (mobileMenuFocusRevision.get(menu) || 0) + 1;
        mobileMenuFocusRevision.set(menu, revision);
        window.setTimeout(() => {
          if (menu.open && mobileMenuMedia.matches && mobileMenuFocusRevision.get(menu) === revision) panelFocusableMenuControls(menu)[0]?.focus({ preventScroll: true });
        }, 0);
      });
    });
    document.querySelectorAll('[data-header-ways]').forEach((ways) => {
      if (initializedWays.has(ways)) return;
      initializedWays.add(ways);
      const toggle = ways.querySelector('[data-header-ways-toggle]');
      const panel = ways.querySelector('[data-header-ways-panel]');
      if (!toggle || !panel) return;
      listen(toggle, 'click', () => {
        const open = panel.hidden;
        initializedWays.forEach((other) => closeWays(other));
        panel.hidden = !open;
        toggle.setAttribute('aria-expanded', String(open));
      });
    });
  };
  listen(document, 'pointerdown', (event) => {
    initializedMenus.forEach((menu) => { if (menu.open && !menu.closest('.site-header')?.contains(event.target)) closeMenu(menu, { restoreFocus: true }); });
    initializedWays.forEach((ways) => { if (!ways.contains(event.target)) closeWays(ways); });
  }, true);
  listen(document, 'click', (event) => {
    const close = event.target.closest?.('[data-mobile-menu-close]');
    initializedMenus.forEach((menu) => {
      if (!menu.open) return;
      if (close && menu.contains(close)) closeMenu(menu, { restoreFocus: true });
      else if (event.target.closest?.('a[href]') && menu.closest('.site-header')?.contains(event.target)) closeMenu(menu);
    });
    if (event.target.closest?.('a[href]')) initializedWays.forEach((ways) => closeWays(ways));
  }, true);
  listen(document, 'focusin', (event) => {
    initializedWays.forEach((ways) => { if (!ways.contains(event.target)) closeWays(ways); });
  });
  listen(document, 'keydown', (event) => {
    const menu = document.querySelector('[data-mobile-menu][open]');
    if (event.key === 'Escape') {
      if (menu) { event.preventDefault(); closeMenu(menu, { restoreFocus: true }); }
      initializedWays.forEach((ways) => {
        if (!ways.querySelector('[data-header-ways-panel]')?.hidden) { event.preventDefault(); closeWays(ways, true); }
      });
      return;
    }
    if (!menu || event.key !== 'Tab') return;
    const controls = focusableMenuControls(menu);
    if (!controls.length) return;
    if (event.shiftKey && isCurrentControl(event.target, controls[0])) { event.preventDefault(); controls[controls.length - 1].focus(); }
    else if (!event.shiftKey && isCurrentControl(event.target, controls[controls.length - 1])) { event.preventDefault(); controls[0].focus(); }
  });
  const onBreakpoint = () => {
    if (mobileMenuMedia.matches) {
      initializedWays.forEach((ways) => {
        const recover = ways.contains(document.activeElement) || ways.closest('.site-header')?.querySelector('.header-nav')?.contains(document.activeElement);
        closeWays(ways);
        if (recover) ways.closest('.site-header')?.querySelector('[data-mobile-menu] > summary')?.focus({ preventScroll: true });
      });
    } else {
      initializedMenus.forEach((menu) => {
        const recover = menu.contains(document.activeElement);
        closeMenu(menu, { desktop: recover });
      });
    }
  };
  mobileMenuMedia.addEventListener?.('change', onBreakpoint);
  cleanups.add(() => mobileMenuMedia.removeEventListener?.('change', onBreakpoint));
  listen(document, 'dreamit:making-help:before-open', () => {
    initializedMenus.forEach((menu) => closeMenu(menu));
    initializedWays.forEach((ways) => closeWays(ways));
  });
  listen(document, 'shopify:section:unload', (event) => {
    ownedListeners.forEach((callbacks, target) => {
      if (target !== document && event.target.contains(target)) Array.from(callbacks).forEach((cleanup) => cleanup());
    });
    initializedMenus.forEach((menu) => { if (event.target.contains(menu)) { closeMenu(menu); initializedMenus.delete(menu); } });
    initializedWays.forEach((ways) => { if (event.target.contains(ways)) { closeWays(ways); initializedWays.delete(ways); } });
  });
  listen(document, 'shopify:section:load', initHeaderMenus);
  window.DIMHeaderMenus = { destroy() {
    initializedMenus.forEach((menu) => closeMenu(menu));
    initializedWays.forEach((ways) => closeWays(ways));
    cleanups.forEach((cleanup) => cleanup());
    initializedMenus.clear(); initializedWays.clear();
  } };
  initHeaderMenus();

  document.addEventListener('keyup', (event) => {
    if (event.key !== 'Escape') return;
    const disclosure = document.activeElement?.closest('details[open]');
    if (!disclosure) return;
    disclosure.removeAttribute('open');
    disclosure.querySelector(':scope > summary')?.focus();
  });

  document.querySelectorAll('[data-sort-select]').forEach((select) => {
    select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      window.location.assign(url.toString());
    });
  });

  document.querySelectorAll('[data-cookie-settings]').forEach((button) => {
    const fallback = button.parentElement?.querySelector('[data-cookie-settings-fallback]');
    button.addEventListener('click', async () => {
      const showPreferences = window.privacyBanner?.showPreferences;
      if (typeof showPreferences !== 'function') {
        if (fallback) fallback.hidden = false;
        return;
      }

      button.disabled = true;
      if (fallback) fallback.hidden = true;
      try {
        await showPreferences.call(window.privacyBanner);
      } catch (error) {
        if (fallback) fallback.hidden = false;
      } finally {
        button.disabled = false;
      }
    });
  });

  const basketFocusKey = 'dim:basket-focus:v1';
  const readBasketFocus = () => {
    try { return JSON.parse(window.sessionStorage.getItem(basketFocusKey) || 'null'); } catch (_) { return null; }
  };
  const writeBasketFocus = (value) => {
    try { window.sessionStorage.setItem(basketFocusKey, JSON.stringify(value)); } catch (_) {}
  };
  const clearBasketFocus = () => {
    try { window.sessionStorage.removeItem(basketFocusKey); } catch (_) {}
  };
  const cartLineForKey = (section, lineKey) => Array.from(section.querySelectorAll('[data-cart-line-key]')).find((line) => line.dataset.cartLineKey === lineKey && line.classList.contains('cart-item'));
  const announceCartChange = (status, message) => {
    if (!status) return;
    status.textContent = '';
    window.setTimeout(() => { status.textContent = message; }, 0);
  };

  document.querySelectorAll('.ux-basket').forEach((section) => {
    const pageWidth = section.querySelector(':scope > .page-width');
    const summary = document.querySelector('.ux-basket-summary');
    if (pageWidth && summary && !pageWidth.contains(summary)) {
      pageWidth.append(summary);
      section.dataset.basketEnhanced = 'true';
    }

    const status = section.querySelector('[data-cart-status]');
    const form = section.querySelector('#cart');

    const restoredFocus = readBasketFocus();
    if (restoredFocus?.action === 'quantity') {
      const line = cartLineForKey(section, restoredFocus.lineKey);
      const input = line?.querySelector('[data-cart-quantity-input]');
      if (input) {
        input.focus({ preventScroll: true });
        announceCartChange(status, `Basket updated. Quantity for ${line.dataset.cartLineLabel} is ${input.value}.`);
      }
      clearBasketFocus();
    } else if (restoredFocus?.action === 'remove') {
      const removals = Array.from(section.querySelectorAll('[data-cart-remove]'));
      const target = removals[restoredFocus.index] || removals[removals.length - 1] || section.querySelector('h1');
      if (target) {
        if (target.tagName === 'H1') target.tabIndex = -1;
        target.focus({ preventScroll: true });
      }
      announceCartChange(status, 'Item removed from your basket.');
      clearBasketFocus();
    }

    form?.addEventListener('submit', (event) => {
      if (event.submitter?.name === 'checkout') return;
      const lineKey = form.dataset.cartFocusLineKey;
      if (lineKey) writeBasketFocus({ action: 'quantity', lineKey });
    });

    section.querySelectorAll('[data-cart-quantity]').forEach((control) => {
      const input = control.querySelector('[data-cart-quantity-input]');
      const line = control.closest('.cart-item');
      if (!input || !line) return;
      const label = line.dataset.cartLineLabel || 'this item';
      const rememberLine = () => {
        if (form) form.dataset.cartFocusLineKey = line.dataset.cartLineKey;
      };
      input.addEventListener('change', rememberLine);
      const adjustQuantity = (change) => {
        const current = Number.parseInt(input.value, 10);
        const quantity = Math.max(1, (Number.isFinite(current) ? current : 1) + change);
        input.value = String(quantity);
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.focus({ preventScroll: true });
        announceCartChange(status, `Quantity for ${label} is now ${quantity}. Select Update basket to save the change.`);
      };
      control.querySelector('[data-cart-quantity-decrease]')?.addEventListener('click', () => adjustQuantity(-1));
      control.querySelector('[data-cart-quantity-increase]')?.addEventListener('click', () => adjustQuantity(1));
    });

    section.querySelectorAll('[data-cart-remove]').forEach((remove, index) => {
      remove.addEventListener('click', () => {
        writeBasketFocus({ action: 'remove', index });
      });
    });
  });
})();
